import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api'; // Ensure this is imported
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { 
  LockClosedIcon, 
  UserIcon,
  EyeIcon,
  EyeSlashIcon 
} from '@heroicons/react/24/outline';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setUser, isAuthenticated } = useAuth(); // Get setters from Context
  const navigate = useNavigate();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  // Handle navigation if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    console.log('🔵 Attempting Login for:', username);

    try {
      // 1. Call the API directly to get the response containing tokens
      const response = await authAPI.login({ username, password });
      
      const { access, refresh, user } = response.data;

      // 2. Save tokens and user data to localStorage
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      localStorage.setItem('user', JSON.stringify(user));

      // 3. Update the AuthContext state immediately
      if (setUser) {
        setUser(user);
      }

      console.log('✅ Login successful, tokens saved.');
      
      // 4. Navigate to dashboard
      navigate('/dashboard');
      
    } catch (err: any) {
      console.error('❌ Login error:', err);
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.detail || 
                      'Login failed. Please check your credentials.';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse: CredentialResponse) => {
    const googleToken = credentialResponse.credential;
    if (!googleToken) {
      setError('Google sign-in did not return a valid token. Please try again.');
      return;
    }

    setError('');
    setGoogleLoading(true);

    try {
      const response = await authAPI.googleLogin(googleToken);
      const { access, refresh, user } = response.data;

      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      localStorage.setItem('user', JSON.stringify(user));

      if (setUser) {
        setUser(user);
      }

      navigate('/dashboard');
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Google login failed. Please try again.';
      setError(errorMsg);
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-red-50 text-slate-900 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 dark:text-slate-100 flex flex-col">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-500 to-red-600 py-8 sm:py-10 lg:py-12 flex-shrink-0">
        <div className="mx-auto max-w-6xl px-4 text-center sm:px-6 lg:px-8">
          {/* Logo */}
          <div className="mb-3 sm:mb-4 flex justify-center">
            <img 
              src="/SOMS_logo.png" 
              alt="SOMS Logo" 
              className="h-12 sm:h-16 w-12 sm:w-16 rounded-2xl object-contain drop-shadow-lg" 
            />
          </div>
          
          {/* Main Title - Full Abbreviation */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-lg px-2">
            Studio Operations Management System
          </h1>
          
          {/* Tagline */}
          <p className="mt-2 sm:mt-3 text-xs sm:text-sm font-medium text-blue-50 drop-shadow-md max-w-2xl mx-auto px-2">
            Professional Studio Management Solution | Real-time Project Tracking | Financial Analytics
          </p>
        </div>
      </div>

      {/* Login Card Section with Shadow Effect */}
      <div className="relative mx-auto max-w-6xl w-full px-4 py-8 sm:py-12 sm:px-6 lg:px-8 flex-grow flex flex-col justify-center">
        {/* Decorative shadow blur effect above card */}
        <div className="absolute inset-x-0 -top-8 mx-auto h-20 w-3/4 bg-gradient-to-b from-red-500/20 to-transparent blur-3xl pointer-events-none hidden sm:block" />
        
        <div className="relative grid w-full grid-cols-1 overflow-hidden rounded-2xl sm:rounded-3xl bg-white shadow-xl sm:shadow-2xl ring-1 ring-black/5 md:grid-cols-2">
          <div className="hidden flex-col bg-gradient-to-br from-blue-600 via-indigo-600 to-red-600 p-8 sm:p-10 text-white md:flex">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
                SOMS Studio Suite
              </div>
              <div className="mt-6 flex items-center gap-3">
                <img 
                  src="/SOMS_logo.png" 
                  alt="SOMS Logo" 
                  className="h-12 w-12 rounded-xl object-contain flex-shrink-0" 
                />
                <div className="min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold leading-tight">Welcome back</h2>
                  <p className="text-xs sm:text-sm text-blue-100">Sign in to your studio workspace</p>
                </div>
              </div>
              <p className="mt-4 text-sm text-blue-50">
                Keep track of sessions, payments, and producer commissions with clarity and speed.
              </p>
            </div>
            <div className="mt-8 sm:mt-10 max-w-md space-y-3 sm:space-y-4 text-sm leading-relaxed text-blue-50">
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-white flex-shrink-0" />
                <p>Secure access for studio owners and producers.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-white flex-shrink-0" />
                <p>UGX-optimized financial summaries and reporting.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2 w-2 rounded-full bg-white flex-shrink-0" />
                <p>Fast access to active projects and balances.</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 md:p-10">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-red-600 bg-clip-text text-transparent">Welcome</h1>
              <p className="mt-2 text-xs sm:text-sm text-gray-600">
                Sign in to your studio dashboard and manage operations.
              </p>
            </div>

            <form id="login-form" onSubmit={handleSubmit} className="mt-6 sm:mt-8 space-y-4 sm:space-y-6">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs sm:text-sm text-red-700 break-words">
                  {error}
                </div>
              )}

              <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-blue-50/60 p-4 sm:p-5 shadow-sm ring-1 ring-white">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">Quick Access</p>
                    <p className="mt-1 text-sm sm:text-base font-semibold text-slate-800">Continue with Google</p>
                    <p className="mt-1 text-xs sm:text-sm text-slate-600">Recommended for studio owners to sign in faster and securely.</p>
                  </div>
                  <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] sm:text-xs font-semibold text-emerald-700 whitespace-nowrap">
                    Secure OAuth
                  </span>
                </div>

                <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                  {googleClientId ? (
                    <div className="flex items-center justify-center">
                      <GoogleLogin
                        onSuccess={handleGoogleLogin}
                        onError={() => setError('Google sign-in was cancelled or failed.')}
                        useOneTap={false}
                        text="continue_with"
                        theme="outline"
                        shape="pill"
                        size="large"
                        width="320"
                      />
                    </div>
                  ) : (
                    <div className="rounded-lg border border-amber-300 bg-amber-50/90 px-3 py-2.5 text-xs text-amber-800">
                      Google sign-in is not configured yet. Add <span className="font-semibold">REACT_APP_GOOGLE_CLIENT_ID</span> to enable it.
                    </div>
                  )}
                  {googleLoading && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-xs text-slate-600">
                      <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                      Completing Google sign-in...
                    </div>
                  )}
                </div>
              </div>

              <div className="relative pt-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200" />
                </div>
                <div className="relative flex justify-center text-xs text-slate-500">
                  <span className="bg-white px-3.5 font-medium">or continue with username and password</span>
                </div>
              </div>

              {/* Username Field */}
              <div>
                <label htmlFor="username" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    className="block w-full rounded-lg border border-gray-300 bg-white px-10 py-2 sm:py-2.5 text-sm text-gray-900 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <div className="flex items-center justify-between mb-1 sm:mb-2">
                  <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-xs sm:text-sm text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LockClosedIcon className="h-4 sm:h-5 w-4 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="block w-full rounded-lg border border-gray-300 bg-white px-10 py-2 sm:py-2.5 text-sm text-gray-900 shadow-sm transition focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                      ) : (
                        <EyeIcon className="h-4 sm:h-5 w-4 sm:w-5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-red-600 px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-bold text-white shadow-lg transition hover:shadow-xl hover:from-blue-700 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 sm:h-5 w-4 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="hidden sm:inline">Signing in...</span>
                      <span className="sm:hidden">Signing in</span>
                    </>
                  ) : (
                    'Sign in to Studio Dashboard'
                  )}
                </button>
              </div>
            </form>

            {/* Divider */}
            <div className="mt-6 sm:mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-xs sm:text-sm">
                  <span className="px-4 bg-white text-gray-500">Don't have an account?</span>
                </div>
              </div>

              <div className="mt-4 sm:mt-6 text-center">
                <Link
                  to="/register"
                  className="inline-flex items-center text-xs sm:text-sm font-medium text-blue-600 hover:text-red-600 group transition"
                >
                  Create studio account
                  <svg className="ml-2 h-4 sm:h-5 w-4 sm:w-5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>

              {/* Staff Login Link */}
              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                <div className="text-center">
                  <p className="text-xs sm:text-sm text-gray-500 mb-2">Team member??</p>
                  <Link
                    to="/staff-login"
                  className="inline-flex items-center text-xs sm:text-sm font-medium text-purple-600 hover:text-purple-700 group transition"
                >
                  Sign in with access key
                  <svg className="ml-2 h-4 sm:h-5 w-4 sm:w-5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
